import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Play, Loader2 } from "lucide-react";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import { IlmekContentPack } from "../../types/ilmek";

export default function IlmekOgrenPage() {
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const [packs, setPacks] = useState<IlmekContentPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingId, setStartingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.getIlmekContentPacks({ onayDurumu: "ONAYLANDI", limit: 100 });
      setPacks(Array.isArray(res?.data) ? res.data : []);
    } catch (error: any) {
      await showAlert({ title: "Hata", message: error?.message || "Dersler yüklenemedi.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    load();
  }, [load]);

  const start = useCallback(
    async (pack: IlmekContentPack) => {
      setStartingId(pack.id);
      try {
        const journey = await ApiService.createIlmekJourney(pack.kazanimKodu);
        navigate(`/ilmek/ogren/${journey.id}`);
      } catch (error: any) {
        await showAlert({ title: "Hata", message: error?.message || "Ders başlatılamadı.", type: "error" });
        setStartingId(null);
      }
    },
    [navigate, showAlert]
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-600" />
          Öğrenme Yolculuğu
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Bir kazanım seç, yapay zekâ öğretmenle adım adım (merak → keşif → öğret → hatırla → aktar → görev) öğren.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : packs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          Henüz onaylı ders yok. Öğretmenin paket onaylayınca burada görünecek.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {packs.map((pack) => (
            <div key={pack.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col hover:shadow-md transition-shadow">
              <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded self-start">
                {pack.kazanimKodu}
              </span>
              <h3 className="text-base font-semibold text-gray-900 mt-2 break-words">{pack.baslik || "(başlıksız)"}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {[pack.ders, pack.sinif ? `${pack.sinif}. sınıf` : null, pack.unite].filter(Boolean).join(" · ")}
              </p>
              <button
                onClick={() => start(pack)}
                disabled={startingId === pack.id}
                className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                {startingId === pack.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {startingId === pack.id ? "Başlatılıyor..." : "Öğrenmeye Başla"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
